import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Filter,
  Flame,
  Sun,
  Snowflake,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Send,
  Phone,
} from "lucide-react";
import useCrmApi from "../../../components/hook/crm-req";
import useRequest from "../../../components/hook/use-req";
import { ContactModal } from "../../../components/crm/customer/update-customer";
import ReuseableDrawer from "../../../components/resuable-drawer";
import { TeamDetails } from "../../../components/crm/customer/types";
import {
  Temperature,
  LeadRecord,
  TEMPERATURE_STYLES,
  StatusPill,
  ChannelLabel,
  goToLeadConversation,
  LeadTableRow,
  mapRecordToLead,
  recordToContactItem,
  updateLeadSafely,
} from "./shared";
import LeadDetailsModal from "./lead-details-modal";
import { AssignAgentModal } from "./assign-agent-modal";
import { NewTagModal } from "../../../components/crm/conversation/components/insight";
import ConversationAgentLeads from "./conversation-agent-leads";
import { isConversationAgent } from "../../../utils/org-role";

const TEMPERATURE_RANK: Record<Temperature, number> = { hot: 0, warm: 1, cold: 2, new: 3 };

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

type DashboardView = "realtime" | "fixed";

// ─────────────────────────────────────────────────────────────
// Small UI atoms
// ─────────────────────────────────────────────────────────────

const StatTile: React.FC<{
  icon: React.ReactNode;
  iconColor: string;
  label: string;
  value: number;
}> = ({ icon, iconColor, label, value }) => (
  <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 flex flex-col gap-3 min-w-0">
    <div className="flex items-center gap-2 min-w-0">
      <span className={`shrink-0 ${iconColor}`}>{icon}</span>
      <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 leading-tight truncate">{label}</span>
    </div>
    <span className="text-2xl font-semibold text-indigo-600 dark:text-indigo-400 underline decoration-2 underline-offset-2">{value}</span>
  </div>
);

const ViewToggle: React.FC<{ value: DashboardView; onChange: (v: DashboardView) => void }> = ({ value, onChange }) => (
  <div className="flex items-center gap-2 shrink-0">
    {/* {(["realtime", "fixed"] as DashboardView[]).map((v) => (
      <button
        key={v}
        onClick={() => onChange(v)}
        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
          value === v ? "bg-gray-900 text-white" : "bg-indigo-50 text-gray-500 hover:bg-indigo-100"
        }`}
      >
        {v === "realtime" ? "Real-time" : "Fixed Period"}
      </button>
    ))} */}
  </div>
);

// ── Date range filter — sends `from_date`/`to_date` to /crm/unified so the
// stat tiles' backend `summary` reflects the selected period. Param names are
// a best-effort match to the one confirmed date param (`from_date`) already
// used elsewhere for leads; flag to backend if `to_date` isn't the real name.
const DateRangeFilter: React.FC<{
  startDate: string;
  endDate: string;
  onApply: (startDate: string, endDate: string) => void;
}> = ({ startDate, endDate, onApply }) => {
  const [open, setOpen] = useState(false);
  const [draftStart, setDraftStart] = useState(startDate);
  const [draftEnd, setDraftEnd] = useState(endDate);

  useEffect(() => {
    if (open) {
      setDraftStart(startDate);
      setDraftEnd(endDate);
    }
  }, [open, startDate, endDate]);

  const label = startDate && endDate ? `${startDate} – ${endDate}` : "All time";

  return (
    <div className="relative shrink-0">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 border border-gray-200 dark:border-gray-700 rounded-lg px-3.5 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 bg-white dark:bg-gray-900 whitespace-nowrap"
      >
        <Calendar size={14} className="text-[#7B7B7B] font-semibold dark:text-gray-500" />
        {label}
        <ChevronDown size={14} className="text-[#7B7B7B] font-semibold dark:text-gray-500" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-11 z-20 w-72 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg p-4 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Start Date</label>
                <input
                  type="date"
                  value={draftStart}
                  onChange={(e) => setDraftStart(e.target.value)}
                  className="border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-indigo-400"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400">End Date</label>
                <input
                  type="date"
                  value={draftEnd}
                  onChange={(e) => setDraftEnd(e.target.value)}
                  className="border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-indigo-400"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => { onApply(draftStart, draftEnd); setOpen(false); }}
                className="flex-1 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors"
              >
                Apply
              </button>
              <button
                onClick={() => { onApply("", ""); setOpen(false); }}
                className="flex-1 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Clear
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Recommendation card (carousel)
// ─────────────────────────────────────────────────────────────

const RecommendationCard: React.FC<{ lead: LeadRecord; onMessage: () => void; onViewDetails: () => void }> = ({
  lead,
  onMessage,
  onViewDetails,
}) => {
  const [expanded, setExpanded] = useState(false);
  const temp = TEMPERATURE_STYLES[lead.temperature];

  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 flex flex-col gap-3 min-w-0">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-9 h-9 rounded-full bg-indigo-100 dark:bg-indigo-500/20 flex items-center justify-center text-indigo-600 dark:text-indigo-400 text-xs font-semibold shrink-0">
            {lead.initials}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{lead.display_name}</p>
            <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 truncate">{lead.phone || "No phone on file"}</p>
          </div>
        </div>
      </div>
      <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full w-fit ${temp.bg} ${temp.text}`}>
        <span className="w-1.5 h-1.5 rounded-full bg-current" />
        {temp.label}
      </span>

      <button
        onClick={() => setExpanded((e) => !e)}
        className="flex items-center justify-between text-xs font-medium text-gray-600 dark:text-gray-400 bg-transparent border-none cursor-pointer p-0"
      >
        Contact Info
        <ChevronDown size={14} className={`text-[#7B7B7B] font-semibold dark:text-gray-500 transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>
      {expanded && (
        <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1 -mt-1">
          <div className="flex justify-between"><span>Email</span><span className="text-gray-800 dark:text-gray-200 truncate max-w-[140px]">{lead.email || "N.A."}</span></div>
          <div className="flex justify-between"><span>Channel</span><ChannelLabel channel={lead.channel} /></div>
          <div className="flex justify-between"><span>Status</span><StatusPill status={lead.status} /></div>
        </div>
      )}

      <div className="border-t border-gray-100 dark:border-gray-800 pt-3">
        <p className="text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500 mb-2">Next Best Action</p>
        <div className="space-y-1.5">
          <button
            onClick={onMessage}
            className="w-full flex items-center justify-between text-xs font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors bg-white dark:bg-gray-900"
          >
            <span className="flex items-center gap-2">
              <Send size={13} className="text-[#7B7B7B] font-semibold dark:text-gray-500" />
              Send Message
            </span>
            <ChevronRight size={14} className="text-[#7B7B7B] font-semibold dark:text-gray-500" />
          </button>
          <button
            onClick={() => {
              if (!lead.phone) {
                toast.error("This lead has no phone number on file yet.");
                return;
              }
              window.location.href = `tel:${lead.phone}`;
            }}
            className="w-full flex items-center justify-between text-xs font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors bg-white dark:bg-gray-900"
          >
            <span className="flex items-center gap-2">
              <Phone size={13} className="text-[#7B7B7B] font-semibold dark:text-gray-500" />
              Call Contact
            </span>
            <ChevronRight size={14} className="text-[#7B7B7B] font-semibold dark:text-gray-500" />
          </button>
        </div>
      </div>

      <button onClick={onViewDetails} className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 text-left bg-transparent border-none cursor-pointer p-0">
        View Lead Details
      </button>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────

const CARD_PAGE_SIZE = 4;
const RECENT_LEADS_LIMIT = 5;

const LeadsPage: React.FC = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("authToken");

  // ── Overview leads — a broader sample used for the stat tiles, Immediate
  // Recommendations carousel, and the Recent Leads preview. There's no
  // dedicated backend aggregation endpoint for these, so they're derived
  // from a larger real fetch rather than a fabricated summary.
  const [overviewLeads, setOverviewLeads] = useState<LeadRecord[]>([]);
  const [totalLeadsCount, setTotalLeadsCount] = useState(0);
  // Real backend-computed aggregates (across ALL leads, not just the 100-row
  // sample) — null until the response includes a `summary` object, in which
  // case the stat tiles fall back to the client-derived sample counts.
  const [backendSummary, setBackendSummary] = useState<{
    total: number; hot: number; warm: number; cold: number; qualified: number;
  } | null>(null);
  const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [view, setView] = useState<DashboardView>("fixed");
  const [cardPage, setCardPage] = useState(0);
  const [editingLead, setEditingLead] = useState<LeadRecord | null>(null);
  const [detailsLead, setDetailsLead] = useState<LeadRecord | null>(null);
  const [detailsInitialTab, setDetailsInitialTab] = useState<"Details" | "Insight" | "Actions">("Actions");
  const [teamMembers, setTeamMembers] = useState<TeamDetails[]>([]);
  const [tagLead, setTagLead] = useState<LeadRecord | null>(null);
  const [assignLead, setAssignLead] = useState<LeadRecord | null>(null);

  const openLeadDetails = (lead: LeadRecord, tab: "Details" | "Insight" | "Actions" = "Actions") => {
    setDetailsLead(lead);
    setDetailsInitialTab(tab);
  };

  const handleTagsUpdated = (leadId: string, tags: string[]) => {
    setOverviewLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, tags } : l)));
  };

  const { makeRequest: getTeamList } = useRequest(`/profile/users`, "GET", {
    Authorization: `Bearer ${token}`,
  });

  const handleSaveTagsForLead = async (tags: string[]): Promise<boolean> => {
    if (!tagLead) return false;
    const { ok, error } = await updateLeadSafely(tagLead.id, { tags }, token);
    if (ok) {
      handleTagsUpdated(tagLead.id, tags);
      toast.success("Tags saved");
      return true;
    }
    toast.error(error || "Failed to save tags");
    return false;
  };

  useEffect(() => {
    (async () => {
      const [response] = await getTeamList();
      if (Array.isArray(response)) setTeamMembers(response);
      else if (Array.isArray(response?.users)) setTeamMembers(response.users);
      else if (Array.isArray(response?.results)) setTeamMembers(response.results);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Best-effort: stored under `custom.assigned_to` via the same /leads PUT
  // used for tags — not a backend-confirmed field yet. updateLeadSafely
  // fetches the latest lead first since PUT /leads/{id} is a full replace —
  // sending only { custom } would reset score/status/etc. to their defaults.
  const handleAssign = async (lead: LeadRecord, member: TeamDetails | null) => {
    const { ok, error } = await updateLeadSafely(lead.id, { custom: { assigned_to: member?.id ?? null } }, token);
    if (ok) {
      setOverviewLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, assignedTo: member?.id ?? null } : l)));
      toast.success(member ? `Assigned to ${member.first_name} ${member.last_name}` : "Unassigned");
    } else {
      toast.error(error || "Failed to update assignee");
    }
  };

  const { makeRequest: getOverviewLeads } = useCrmApi("/crm/unified", "GET", {
    Authorization: `Bearer ${token}`,
  });

  const loadOverviewLeads = async (range: { startDate: string; endDate: string } = dateRange) => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, unknown> = {
        type: "lead",
        sort_order: "desc",
        page: 1,
        per_page: 100,
      };
      if (range.startDate) params.from_date = range.startDate;
      if (range.endDate) params.to_date = range.endDate;

      const [response, status] = await getOverviewLeads(undefined, params);
      if (status !== 200 || !response?.results) {
        throw new Error(response?.message || "Failed to fetch leads");
      }
      setOverviewLeads(response.results.map(mapRecordToLead));
      // The real total lead count (backend-side, not capped by our 100-row
      // sample) — `total` and `counts.leads` are the same number in practice,
      // fall back between them just in case one is missing on a given response.
      setTotalLeadsCount(response.total ?? response.counts?.leads ?? response.results.length);
      // Real backend-computed aggregates across ALL leads, when present.
      setBackendSummary(response.summary ? {
        total: response.summary.total_leads,
        hot: response.summary.hot_leads,
        warm: response.summary.warm_leads,
        cold: response.summary.cold_leads,
        qualified: response.summary.qualified_leads,
      } : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  const applyDateRange = (startDate: string, endDate: string) => {
    const range = { startDate, endDate };
    setDateRange(range);
    loadOverviewLeads(range);
  };

  useEffect(() => {
    loadOverviewLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Stat tiles — real counts only. No fabricated week-over-week deltas:
  // there's no previous-period lead count available from this API yet.
  // Contacted/Converted were dropped: real lead `status` values seen from
  // the backend are only new/qualified/hot/warm — nothing ever matches
  // "contact"/"convert", so those tiles were permanently stuck at 0.
  const stats = useMemo(() => {
    if (backendSummary) return backendSummary;
    // Fallback for responses without a `summary` object — derived from
    // whatever was fetched for the overview sample (capped at 100), so
    // these are only as complete as that sample when the real total
    // exceeds it. Total still uses the real backend count either way.
    const total = totalLeadsCount;
    const hot = overviewLeads.filter((l) => l.temperature === "hot").length;
    const warm = overviewLeads.filter((l) => l.temperature === "warm").length;
    const cold = overviewLeads.filter((l) => l.temperature === "cold" || l.temperature === "new").length;
    const qualified = overviewLeads.filter((l) => (l.status || "").toLowerCase() === "qualified").length;
    return { total, hot, warm, cold, qualified };
  }, [overviewLeads, totalLeadsCount, backendSummary]);

  // ── Recommendations: hot > warm > cold > new, real leads only.
  const recommendedLeads = useMemo(() => {
    return [...overviewLeads].sort((a, b) => TEMPERATURE_RANK[a.temperature] - TEMPERATURE_RANK[b.temperature]).slice(0, 12);
  }, [overviewLeads]);

  const cardPageCount = Math.max(1, Math.ceil(recommendedLeads.length / CARD_PAGE_SIZE));
  const visibleCards = recommendedLeads.slice(cardPage * CARD_PAGE_SIZE, cardPage * CARD_PAGE_SIZE + CARD_PAGE_SIZE);

  // ── Recent Leads — hot first, cold last, real leads only.
  const recentLeads = useMemo(() => {
    return [...overviewLeads]
      .sort((a, b) => TEMPERATURE_RANK[a.temperature] - TEMPERATURE_RANK[b.temperature])
      .slice(0, RECENT_LEADS_LIMIT);
  }, [overviewLeads]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <p className="text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">Loading leads…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <p className="text-sm text-red-500 mb-3">{error}</p>
          <button onClick={() => loadOverviewLeads()} className="text-xs px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
            Retry
          </button>
        </div>
      </div>
    );
  }

  // conversation_agent gets a single-page, fully-paginated leads view (no
  // carousel, no 5-row "Recent Leads" preview) — see conversation-agent-leads.tsx.
  // Checked here (after every hook above has already run unconditionally)
  // rather than as an early return at the top of the component, since React
  // requires hooks to run in the same order on every render.
  if (isConversationAgent()) {
    return <ConversationAgentLeads />;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-4 sm:p-6">
      {/* Drawers/modals below are deliberately siblings of this space-y-6
          wrapper, not children of it — Tailwind's space-y-* adds a top
          margin to every non-first child, which was shifting the drawer's
          fixed, full-screen backdrop down by that same gap. */}
      <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">Leads</h1>
        <div className="flex items-center gap-3">
          <ViewToggle value={view} onChange={setView} />
          <DateRangeFilter
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onApply={applyDateRange}
          />
        </div>
      </div>

      {view === "realtime" ? (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-10 text-center text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">
          Real-time lead activity isn't available yet.
        </div>
      ) : (
        <>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Lead Performance</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Find, engage and convert your potential customers</p>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-4">
            <StatTile icon={<Filter size={18} />} iconColor="text-gray-500" label="Total Leads" value={stats.total} />
            <StatTile icon={<Flame size={18} />} iconColor="text-red-500" label="Hot" value={stats.hot} />
            <StatTile icon={<Sun size={18} />} iconColor="text-amber-500" label="Warm" value={stats.warm} />
            <StatTile icon={<Snowflake size={18} />} iconColor="text-blue-500" label="Cold/ Nurture" value={stats.cold} />
            <StatTile icon={<CheckCircle2 size={18} />} iconColor="text-green-500" label="Qualified" value={stats.qualified} />
          </div>

          {recommendedLeads.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Immediate Recommendations ({recommendedLeads.length})</h3>
                  <p className="text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">Leads that need your attention right now</p>
                </div>
                {cardPageCount > 1 && (
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => setCardPage((p) => Math.max(0, p - 1))}
                      disabled={cardPage === 0}
                      className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800 bg-white dark:bg-gray-900"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <button
                      onClick={() => setCardPage((p) => Math.min(cardPageCount - 1, p + 1))}
                      disabled={cardPage >= cardPageCount - 1}
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-900 dark:bg-gray-700 text-white disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                {visibleCards.map((lead) => (
                  <RecommendationCard
                    key={lead.id}
                    lead={lead}
                    onMessage={() => goToLeadConversation(navigate, lead)}
                    onViewDetails={() => openLeadDetails(lead, "Actions")}
                  />
                ))}
              </div>
            </div>
          )}

          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 sm:p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Recent Leads</h3>
                <p className="text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">Browse your latest leads</p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-gray-800">
                    <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Contact Name</th>
                    <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Phone</th>
                    <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Source</th>
                    <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Conversation Tags</th>
                    <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Deal Status</th>
                    <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Assignee</th>
                    <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recentLeads.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center py-10 text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">
                        No leads found yet.
                      </td>
                    </tr>
                  ) : (
                    recentLeads.map((lead) => (
                      <LeadTableRow
                        key={lead.id}
                        lead={lead}
                        onView={() => goToLeadConversation(navigate, lead)}
                        onEdit={() => setEditingLead(lead)}
                        onOpenDetails={(tab) => openLeadDetails(lead, tab)}
                        onManageTags={setTagLead}
                        teamMembers={teamMembers}
                        onOpenAssign={setAssignLead}
                      />
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {overviewLeads.length > 0 && (
              <div className="pt-4">
                <Link to="/leads/all" className="text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300">
                  Show All Leads
                </Link>
              </div>
            )}
          </div>
        </>
      )}
      </div>

      <LeadDetailsModal
        lead={detailsLead}
        initialTab={detailsInitialTab}
        onClose={() => setDetailsLead(null)}
        onTagsUpdated={handleTagsUpdated}
        teamMembers={teamMembers}
      />

      <NewTagModal
        isOpen={!!tagLead}
        onClose={() => setTagLead(null)}
        existingTags={tagLead?.tags || []}
        onSave={handleSaveTagsForLead}
      />

      <AssignAgentModal
        lead={assignLead}
        teamMembers={teamMembers}
        onClose={() => setAssignLead(null)}
        onAssign={handleAssign}
      />

      <ReuseableDrawer isOpen={!!editingLead} onClose={() => setEditingLead(null)} title="Update Lead" initialWidth="30%">
        <ContactModal
          mode="lead"
          variant="drawer"
          isOpen={!!editingLead}
          onClose={() => setEditingLead(null)}
          contact={editingLead ? recordToContactItem(editingLead) : null}
          onUpdated={() => {
            setEditingLead(null);
            loadOverviewLeads();
          }}
        />
      </ReuseableDrawer>
    </div>
  );
};

export default LeadsPage;
